import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import OnboardingBanner from '../components/OnboardingBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlayerTraining = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isParent = user?.role === 'Parent';
  const [sessions, setSessions] = useState([]);
  const [athlete, setAthlete] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
    fetchSessions();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/athletes`);
      if (response.data.length > 0) {
        const athleteData = response.data[0];
        setAthlete(athleteData);
        fetchAttendance(athleteData.id);
      }
    } catch (error) {
      toast.error('Failed to fetch profile');
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/training-sessions`);
      // Filter upcoming sessions
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const upcoming = response.data.filter(session => {
        const sessionDate = new Date(session.date);
        return sessionDate >= today;
      });
      setSessions(upcoming);
    } catch (error) {
      toast.error('Failed to fetch training schedule');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async (athleteId) => {
    try {
      const response = await axios.get(`${API}/attendance/athlete/${athleteId}`);
      setAttendance(response.data);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    }
  };

  const getSessionForAttendance = (record) => {
    const allSessions = sessions;
    return allSessions.find(s => s.id === record.training_session_id);
  };

  const getStatusBadge = (status) => {
    const colors = {
      present: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      absent: 'bg-red-100 text-red-800 border-red-200',
      injured: 'bg-orange-100 text-orange-800 border-orange-200',
      excused: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700';
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Training Schedule & Attendance
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">
            {isParent ? "View upcoming sessions and your child's attendance history" : 'View upcoming sessions and your attendance history'}
          </p>
        </div>

        <OnboardingBanner
          id={isParent ? "parent-training-v1" : "player-training-v1"}
          description={isParent
            ? "Upcoming sessions and your child's attendance history appear here. Attendance is recorded by your coach after each session."
            : "Upcoming sessions scheduled by your coach, plus your attendance history. Check here before each session."}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upcoming Sessions */}
          <div>
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Upcoming Training Sessions
            </h3>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : sessions.length === 0 ? (
              <Card data-testid="no-sessions-message">
                <CardContent className="py-8 text-center">
                  <p className="text-gray-500 dark:text-gray-500">No upcoming training sessions</p>
                  <p className="text-xs text-gray-400 dark:text-gray-600 mt-1">Your coach will schedule sessions here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="upcoming-sessions">
                {sessions.map((session) => (
                  <Card key={session.id} data-testid={`session-card-${session.id}`}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">{session.title}</CardTitle>
                      <CardDescription>
                        {new Date(session.date).toLocaleDateString()} at {session.time}
                      </CardDescription>
                    </CardHeader>
                    {session.description && (
                      <CardContent className="pt-0">
                        <p className="text-sm text-gray-600 dark:text-gray-500">{session.description}</p>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Attendance History */}
          <div>
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isParent ? "Attendance History" : "My Attendance History"}
            </h3>
            {!athlete ? (
              <Card data-testid="no-athlete-message">
                <CardContent className="py-8 text-center">
                  <p className="text-gray-500 dark:text-gray-500">Profile not linked yet</p>
                </CardContent>
              </Card>
            ) : attendance.length === 0 ? (
              <Card data-testid="no-attendance-message">
                <CardContent className="py-8 text-center">
                  <p className="text-gray-500 dark:text-gray-500">No attendance records yet</p>
                  <p className="text-xs text-gray-400 dark:text-gray-600 mt-1">Records appear here after sessions you attend.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="attendance-history">
                {attendance.map((record) => (
                  <Card key={record.id} data-testid={`attendance-record-${record.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-base">
                            {new Date(record.marked_at).toLocaleDateString()}
                          </CardTitle>
                          <CardDescription className="text-xs">
                            Session ID: {record.training_session_id.substring(0, 8)}...
                          </CardDescription>
                        </div>
                        <Badge className={getStatusBadge(record.status)} data-testid={`attendance-status-${record.id}`}>
                          {record.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    {record.note && (
                      <CardContent className="pt-0">
                        <p className="text-sm text-gray-600 dark:text-gray-500">Note: {record.note}</p>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PlayerTraining;
